import React from "react";
import RichText from "@ui/rich-text";
import { IconTick } from "@ui/icons";
import ComplexText from "@ui/complex-text";

const PointLink = ({ data }) => {
    return (
        <a
            className="inline-block mt-2 text-sm text-skin-primary"
            href={data.link.url}
        >
            {data.link.text}
        </a>
    );
};

const PointSection = ({ data }) => {
    return (
        <div className="mt-4 text-sm">
            <h3 className="text-skin-dark text-sm">{data.title}</h3>
            <RichText items={data.texts} className="mt-1" />
            {data.link && <PointLink data={data} />}
        </div>
    );
};

const PointSections = ({ data }) => {
    if (!data) return null;
    return data.map((section, index) => (
        <PointSection key={index} data={section} />
    ));
};

const PointCheckDefault = ({ data }) => {
    return data.completed ? (
        <div className="bg-[#EFF8EC] absolute w-[36px] h-[36px] rounded-full z-10 top-0 text-center text-sm leading-[28px] font-semibold text-skin-dark border-[#EFF8EC] border-4 text-[#3B7D25] flex items-center justify-center content-center">
            <IconTick className="w-4 h-4" />
        </div>
    ) : (
        <div className="bg-skin-blended absolute w-[36px] h-[36px] rounded-full z-10 top-0 left-[3px] text-center text-sm leading-[28px] font-semibold text-skin-primary border-skin-blended border-4">
            {data.index}
        </div>
    );
};

const PointCheckAlternative = ({ data }) => {
    return data.completed ? (
        <div className="absolute w-[36px] h-[36px] z-10 top-0 left-[3px] text-center text-sm leading-[28px] font-semibold text-skin-primary flex items-center justify-center content-center">
            <IconTick className="w-4 h-4" />
        </div>
    ) : (
        <div className="bg-white absolute w-[36px] h-[36px] rounded-full z-10 top-0 left-[3px] text-center text-sm leading-[28px] font-semibold text-skin-dark border-white border-4">
            {data.index}
        </div>
    );
};

const PointCheckGrayscale = ({ data }) => {
    return data.completed ? (
        <div className="bg-[#EFF8EC] absolute w-[36px] h-[36px] rounded-full z-10 top-0 left-[3px] text-center text-sm leading-[28px] font-semibold text-skin-dark border-[#EFF8EC] border-4 text-[#3B7D25] flex items-center justify-center content-center">
            <IconTick className="w-4 h-4" />
        </div>
    ) : (
        <div className="bg-gray-50 absolute w-[36px] h-[36px] rounded-full z-10 top-0 left-[3px] text-center text-sm leading-[28px] font-semibold text-skin-dark border-white border-4">
            {data.index}
        </div>
    );
};

const PointCheck = ({ data, theme }) => {
    if (theme === "alternative") {
        return <PointCheckAlternative data={data} />;
    } else if (theme === "grayscale") {
        return <PointCheckGrayscale data={data} />;
    }
    return <PointCheckDefault data={data} />;
};

const Point = ({ data, theme }) => {
    const titleCss = `text-sm text-skin-dark font-extrabold mt-1 ${
        theme == "alternative" ? "" : data.completed ? "text-[#3B7D25]" : ""
    }`;
    return (
        <div className="relative flex w-full h-full">
            <PointCheck data={data} theme={theme} />
            <div className="w-1/6 sm:w-2/12 max-w-[50px] min-w-[40px]"></div>
            <div className="w-5/6 sm:w-10/12">
                <h2 className={titleCss}>{data.title}</h2>
                <div className={`text-sm ${data.title ? "mt-3" : ""}`}>
                    {data.texts && <RichText items={data.texts} />}
                    {data.complexTexts && (
                        <ComplexText values={data.complexTexts} />
                    )}
                </div>
                {data.link && <PointLink data={data} />}
                <PointSections data={data.sections} />
            </div>
        </div>
    );
};

const Points = ({ data, theme }) => {
    if (!data) return null;
    return (
        <div className="mt-5 h-full">
            {data.map((item, index) => (
                <Point
                    key={index}
                    data={item}
                    theme={theme}
                />
            ))}
        </div>
    );
};

const InstructionPointsWidget = ({ data }) => {
    return (
        <div className="max-w-lg md:min-w-[300px]">
            {data.heading && (
                <h2 className="text-lg text-skin-dark font-semibold">
                    {data.heading}
                </h2>
            )}
            <RichText items={data.texts} />
            <Points data={data.points} theme={data.theme} />
        </div>
    );
};

export default InstructionPointsWidget;
